import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { projectApi } from "../../api/projectApi";
import { useAuth } from "../../context/AuthContext";
import "./ProjectsListPage.css";

// ── 카테고리 메타데이터 ──────────────────────────────────────
const CATEGORIES = [
  { value: "", label: "All Projects" },
  { value: "dev", label: "Development" },
  { value: "security", label: "Security" },
];

// ── 프로젝트 카드 (Clean & Minimal 디자인) ──────────────────
const ProjectCard = ({ project, onClick }) => {
  const tech = Array.isArray(project.tech)
    ? project.tech
    : JSON.parse(project.tech || "[]");

  return (
    <article className="project-card-clean" onClick={onClick}>
      <div className="card-img-box">
        {project.thumbnail_url ? (
          <img src={project.thumbnail_url} alt={project.title} className="card-img" />
        ) : (
          <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", color:"#cbd5e1", fontSize:"14px", fontWeight:600 }}>
            No Thumbnail
          </div>
        )}
      </div>

      <div className="card-info-box">
        <div className="card-top">
          <div style={{ display:"flex", gap:"6px" }}>
            {(Array.isArray(project.category) ? project.category : [project.category || "dev"]).map(c => (
              <span key={c} className="card-cat">
                {c === "dev" ? "개발" : "보안"}
              </span>
            ))}
          </div>
          <span className="card-date">{project.period || "진행 중"}</span>
        </div>

        <h2 className="card-h2">{project.title}</h2>
        <p className="card-desc">{project.summary}</p>
        
        <div className="card-meta-info">
          {project.role && (
            <div className="card-role-label">
              담당: {project.role}
            </div>
          )}
          <div className="card-tech-list">
            {tech.slice(0, 4).map((item) => (
              <span key={item} className="tech-tag">{item}</span>
            ))}
            {tech.length > 4 && (
              <span className="tech-tag">+{tech.length - 4}</span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

const ProjectsListPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin } = useAuth();

  const [projects, setProjects] = useState([]);
  const [tags, setTags]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "");
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const search    = searchParams.get("search")   || "";
  const category  = searchParams.get("category") || "";
  const activeTag = searchParams.get("tag")      || "";
  const page      = Number(searchParams.get("page") || 1);
  const LIMIT     = 9;

  // 태그 목록 로드
  useEffect(() => {
    projectApi.getTags().then(({ data }) => setTags(data)).catch(() => {});
  }, []);

  // 프로젝트 목록 로드
  useEffect(() => {
    setLoading(true);
    projectApi
      .getList({ search, category, tag: activeTag, page, limit: LIMIT })
      .then(({ data }) => {
        setProjects(data.projects);
        setTotal(data.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search, category, activeTag, page]);

  const updateParams = useCallback(
    (updates) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([k, v]) => {
        if (v) next.set(k, v);
        else next.delete(k);
      });
      next.delete("page");
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const handleSearch   = (q) => { setSearchInput(q); updateParams({ search: q }); };
  const handleCategory = (c) => updateParams({ category: c });
  const handleTag = (t) => {
    let nextTags = activeTag ? activeTag.split(",") : [];
    if (t === "") {
      nextTags = [];
    } else {
      if (nextTags.includes(t)) {
        nextTags = nextTags.filter(x => x !== t);
      } else {
        nextTags.push(t);
      }
    }
    updateParams({ tag: nextTags.join(",") });
  };
  const handlePage     = (p) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", p);
    setSearchParams(next);
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="projects-page-wrap">
      {/* ── 메인 섹션 ── */}
      <section className="projects-section">
        <div className="container">
          <div className="projects-header-min">
            <div>
              <h1 className="page-title">Projects</h1>
              <p style={{ fontSize:"13px", color:"#94a3b8", marginTop:"4px", fontWeight:500 }}>총 {total}개의 프로젝트가 있습니다.</p>
            </div>
            <div className="projects-header-right">
              <form
                className="search-wrap"
                onSubmit={(e) => { e.preventDefault(); handleSearch(searchInput); }}
              >
                <input
                  type="text"
                  className="search-input"
                  placeholder="프로젝트 검색..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </form>
              {isAdmin && (
                <button
                  className="new-btn"
                  onClick={() => navigate("/projects/write")}
                >
                  새 프로젝트 작성
                </button>
              )}
            </div>
          </div>
          
          <div className="projects-main-layout">
            <div className="projects-content">
              {/* 프로젝트 리스트 */}
              {loading ? (
                <div style={{ textAlign:"center", padding:"100px 0", color:"#94a3b8" }}>프로젝트를 불러오고 있습니다...</div>
              ) : projects.length === 0 ? (
                <div style={{ textAlign:"center", padding:"100px 0", color:"#94a3b8" }}>등록된 프로젝트가 없습니다.</div>
              ) : (
                <>
                  <div className="projects-grid">
                    {projects.map((p) => (
                      <ProjectCard
                        key={p.id}
                        project={p}
                        onClick={() => navigate(`/projects/${p.id}`)}
                      />
                    ))}
                  </div>

                  {/* 페이지네이션 */}
                  {totalPages > 1 && (
                    <div className="pagination">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                        <button
                          key={p}
                          className={`pg-btn ${p === page ? "active" : ""}`}
                          onClick={() => handlePage(p)}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <aside className="projects-sidebar desktop-only">
              <SidebarContent 
                categories={CATEGORIES}
                activeCategory={category}
                handleCategory={handleCategory}
                tags={tags}
                activeTag={activeTag}
                handleTag={handleTag}
              />
            </aside>
          </div>

          {/* ── 모바일 전용: 플로팅 필터 버튼 & 바텀 시트 ── */}
          <button className="filter-fab" onClick={() => setIsFilterOpen(true)} aria-label="필터 열기">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
            필터
          </button>

          {isFilterOpen && (
            <div className="bottom-sheet-overlay" onClick={() => setIsFilterOpen(false)}>
              <div className="bottom-sheet" onClick={(e) => e.stopPropagation()}>
                <div className="bottom-sheet-header">
                  <div className="bottom-sheet-handle"></div>
                  <button className="close-sheet" onClick={() => setIsFilterOpen(false)}>&times;</button>
                </div>
                <div className="bottom-sheet-body">
                  <SidebarContent 
                    categories={CATEGORIES}
                    activeCategory={category}
                    handleCategory={handleCategory}
                    tags={tags}
                    activeTag={activeTag}
                    handleTag={handleTag}
                  />
                  <div style={{ marginTop: "24px" }}>
                    <button className="apply-btn" onClick={() => setIsFilterOpen(false)}>필터 적용</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

// ── 사이드바/바텀시트 공통 컨텐츠 ──
const SidebarContent = ({ categories, activeCategory, handleCategory, tags, activeTag, handleTag }) => (
  <>
    <div className="sidebar-section">
      <h3 className="sidebar-h3">카테고리</h3>
      <div className="sidebar-list">
        {categories.map(cat => (
          <button
            key={cat.value}
            className={`sidebar-item ${activeCategory === cat.value ? "active" : ""}`}
            onClick={() => handleCategory(cat.value)}
          >
            {cat.label === "All Projects" ? "전체 프로젝트" :
             cat.label === "Development" ? "개발" :
             cat.label === "Security" ? "보안" : cat.label}
          </button>
        ))}
      </div>
    </div>

    <div className="sidebar-section">
      <h3 className="sidebar-h3">기술 태그</h3>
      <div className="sidebar-tags">
        {tags.map(t => {
          const isActive = activeTag ? activeTag.split(",").includes(t.tag_name) : false;
          return (
            <button
              key={t.tag_name}
              className={`tag-btn ${isActive ? "active" : ""}`}
              onClick={() => handleTag(t.tag_name)}
            >
              #{t.tag_name}
            </button>
          );
        })}
      </div>
    </div>
  </>
);

export default ProjectsListPage;
